import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const LanguageModal = ({ isOpen, onClose, languages = [] }) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Restore focus to whatever triggered this modal once it closes (declared before
  // the effect below so it captures the trigger element BEFORE that effect moves
  // focus onto the close button).
  useModalFocusReturn(isOpen);

  // Lock page scroll while open, without jumping to the top and correctly restoring
  // the exact scroll position on close.
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus({ preventScroll: true });

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
        // Tab trap
        if (e.key === 'Tab') {
          const focusableElements = modalRef.current?.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElements && focusableElements.length > 0) {
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey && document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="language-modal-overlay" onClick={handleBackdropClick}>
      <div className="language-modal-content" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="language-modal-title">
        <div className="language-modal-header">
          <h2 id="language-modal-title" className="language-modal-title">Languages spoken</h2>
          <button
            ref={closeButtonRef}
            className="language-modal-close"
            onClick={onClose}
            aria-label="Close language modal"
          >
            ×
          </button>
        </div>
        
        <div className="language-modal-body">
          {languages && languages.length > 0 ? (
            <p className="language-list">
              {languages.join(', ')}
            </p>
          ) : (
            <p className="language-list">No languages available</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LanguageModal;
